import { h, ic, clear } from "../dom.ts";
import { goHome } from "../main.ts";
import { getMap, spinGlobe } from "../map.ts";
import { catalog, countPlaces, scopeName, KIND_INFO, regionWordPlural, type Catalog } from "../catalog.ts";
import { fmtDist } from "../prefs.ts";
import {
  scopeBBox, scopeSpanKm, scaleFor, ringRadii, effectiveKinds,
  type GameConfig, type PlaceType, type Difficulty, type Scope,
} from "../../../shared/game.ts";

type Tab = "world" | "continent" | "country" | "region";

const POPULAR: { label: string; scope: Scope; kinds?: PlaceType[] }[] = [
  { label: "World capitals", scope: { kind: "world" }, kinds: ["cap"] },
  { label: "Europe", scope: { kind: "continent", name: "Europe" }, kinds: ["cap"] },
  { label: "United States", scope: { kind: "country", code: "US" } },
  { label: "Canada", scope: { kind: "country", code: "CA" } },
  { label: "Japan", scope: { kind: "country", code: "JP" } },
  { label: "Texas", scope: { kind: "country", code: "US", regions: ["US-texas"] } },
];

export interface SetupOpts { mode: "solo" | "room"; config: GameConfig; onDone: (cfg: GameConfig) => void; onBack?: () => void }

export function setupScreen(opts: SetupOpts): HTMLElement {
  const root = h("div", { class: "setup" });
  catalog().then(cat => build(root, cat, opts));
  return root;
}

function build(root: HTMLElement, cat: Catalog, opts: SetupOpts) {
  let cfg: GameConfig = structuredClone(opts.config);
  let tab: Tab = cfg.scope.kind === "country" ? (cfg.scope.regions?.length ? "region" : "country") : cfg.scope.kind;
  spinGlobe(false);

  const panel = h("div", { class: "setup-panel" });
  const side = h("div", { class: "setup-side" });
  root.replaceChildren(panel, side);

  const back = () => (opts.onBack ? opts.onBack() : goHome());
  const startLabel = opts.mode === "solo" ? "Start solo game" : "Save settings";

  function render() {
    clear(panel); clear(side);
    const country = cfg.scope.kind === "country" ? cat.byCountry.get(cfg.scope.code) : undefined;

    // ---- 1. Where
    const tabs = h("div", { class: "seg", role: "group", "aria-label": "Map size" },
      (["world", "continent", "country", "region"] as Tab[]).map(t => h("button", {
        "aria-pressed": String(tab === t),
        onclick: () => {
          tab = t;
          if (t === "world") cfg.scope = { kind: "world" };
          if (t === "continent") cfg.scope = { kind: "continent", name: cfg.scope.kind === "continent" ? cfg.scope.name : "Europe" };
          if (t === "country") cfg.scope = { kind: "country", code: cfg.scope.kind === "country" ? cfg.scope.code : "US" };
          if (t === "region") {
            const code = cfg.scope.kind === "country" ? cfg.scope.code : "US";
            const first = cat.byCountry.get(code)?.regions[0]?.code;
            cfg.scope = { kind: "country", code, regions: cfg.scope.kind === "country" && cfg.scope.regions?.length ? cfg.scope.regions : first ? [first] : [] };
          }
          render();
        },
      }, { world: "World", continent: "Continent", country: "Country", region: "State / Province" }[t])));

    const where = h("section", {}, h("h2", { class: "section-title" }, "Where"), tabs);
    const whereBody = h("div", { style: "display:flex;flex-direction:column;gap:12px;margin-top:12px" });
    where.append(whereBody);

    if (tab === "world") {
      whereBody.append(h("p", { class: "muted", style: "margin:0;font-size:14px" }, "Every country is fair game. The map starts as a globe you can spin."));
    } else if (tab === "continent") {
      whereBody.append(h("div", { class: "chips" }, cat.index.continents.filter(c => c.capitals > 0).map(c => h("button", {
        class: "chip", "aria-pressed": String(cfg.scope.kind === "continent" && cfg.scope.name === c.name),
        onclick: () => { cfg.scope = { kind: "continent", name: c.name }; render(); },
      }, c.name))));
    } else {
      whereBody.append(countryPicker(cat, cfg.scope.kind === "country" ? cfg.scope.code : "US", code => {
        cfg.scope = tab === "region" ? { kind: "country", code, regions: cat.byCountry.get(code)?.regions[0] ? [cat.byCountry.get(code)!.regions[0].code] : [] } : { kind: "country", code };
        if (tab === "region" && !cat.byCountry.get(code)?.regions.length) { tab = "country"; cfg.scope = { kind: "country", code }; }
        render();
      }));
      if (tab === "region" && country) {
        const sel = new Set(cfg.scope.kind === "country" ? cfg.scope.regions ?? [] : []);
        whereBody.append(h("div", { class: "region-list chips", role: "group", "aria-label": `Pick ${regionWordPlural(country)}` },
          country.regions.map(r => h("button", {
            class: "chip", "aria-pressed": String(sel.has(r.code)),
            onclick: () => {
              sel.has(r.code) ? sel.delete(r.code) : sel.add(r.code);
              if (!sel.size) sel.add(r.code);
              cfg.scope = { kind: "country", code: country.code, regions: [...sel] };
              render();
            },
          }, r.name))));
      }
    }
    whereBody.append(h("div", { class: "chips", style: "align-items:center" }, h("span", { class: "muted", style: "font-size:13px" }, "Popular:"),
      POPULAR.map(p => h("button", { class: "chip", style: "height:32px;font-size:13px", onclick: () => {
        cfg.scope = structuredClone(p.scope);
        if (p.kinds) cfg.kinds = [...p.kinds];
        tab = p.scope.kind === "country" ? (p.scope.regions ? "region" : "country") : p.scope.kind;
        render();
      } }, p.label))));

    // ---- 2. What
    const inCountry = cfg.scope.kind === "country";
    const kinds = h("div", { class: "kinds", role: "group", "aria-label": "What to find" },
      (["cap", "rcap", "city", "mark"] as PlaceType[]).map(k => {
        const available = inCountry ? (country?.counts[k] ?? 0) > 0 : k === "cap" || k === "city";
        const on = cfg.kinds.includes(k) && available;
        const label = k === "rcap" && country ? `${country.regionWord} capitals` : KIND_INFO[k].label;
        return h("button", {
          class: "kind", "aria-pressed": String(on), disabled: !available || undefined,
          title: available ? undefined : inCountry ? "None in this country yet" : "Pick a single country for this",
          onclick: () => {
            const set = new Set(cfg.kinds);
            set.has(k) ? set.delete(k) : set.add(k);
            cfg.kinds = [...set];
            if (!effectiveKinds({ ...cfg, kinds: cfg.kinds.filter(x => inCountry || x === "cap" || x === "city") }).length || !cfg.kinds.length) cfg.kinds = [k];
            render();
          },
        }, h("b", {}, label), h("small", {}, k === "city" ? KIND_INFO.city.sub(inCountry) : k === "cap" ? KIND_INFO.cap.sub(inCountry) : KIND_INFO[k].sub(inCountry)));
      }));
    const what = h("section", {}, h("h2", { class: "section-title" }, "What to find (pick any)"), kinds);

    // ---- 3. How hard
    const diff = seg<Difficulty>("Difficulty", [
      ["easy", "Easy", "Famous places"], ["medium", "Medium", "Most people know"],
      ["hard", "Hard", "Lesser-known"], ["expert", "Expert", "Everything"],
    ], cfg.difficulty, v => { cfg.difficulty = v; render(); }, "gold");
    const rounds = seg<number>("Rounds", [[5, "5"], [10, "10"], [15, "15"], [20, "20"]], cfg.rounds, v => { cfg.rounds = v; render(); }, "light");
    const timerOpts: [number, string][] = [[10, "10s"], [20, "20s"], [30, "30s"]];
    if (opts.mode === "solo") timerOpts.push([0, "Off"]);
    if (opts.mode === "room" && cfg.timer === 0) cfg.timer = 20;
    const timer = seg<number>("Timer", timerOpts, cfg.timer, v => { cfg.timer = v; render(); }, "light");
    const how = h("section", {}, h("h2", { class: "section-title" }, "How hard"), diff,
      h("div", { class: "row-opts", style: "margin-top:12px" },
        h("div", { style: "display:flex;align-items:center;gap:8px" }, h("span", { class: "muted", style: "font-size:14px" }, "Rounds"), rounds),
        h("div", { style: "display:flex;align-items:center;gap:8px" }, h("span", { class: "muted", style: "font-size:14px" }, "Timer"), timer),
        opts.mode === "room" ? h("div", { style: "display:flex;align-items:center;gap:8px" }, h("span", { class: "muted", style: "font-size:14px" }, "Pace"),
          seg<"standard" | "blitz">("Pace", [["standard", "Relaxed"], ["blitz", "Blitz"]], cfg.mode, v => { cfg.mode = v; render(); }, "light")) : null));

    const n = countPlaces(cfg, cat);
    const go = () => opts.onDone(structuredClone(cfg));

    panel.append(
      h("div", { class: "setup-head" },
        h("button", { class: "icon-btn", "aria-label": "Back", onclick: back }, ic("back")),
        h("h1", {}, "Choose your map")),
      where, what, how);

    // ---- preview side (desktop) + mobile bottom bar
    const bbox = scopeBBox(cfg.scope, cat.index);
    const sc = scaleFor(scopeSpanKm(bbox));
    const rings = ringRadii(sc);
    side.append(
      h("div", { class: "preview-badge" }, h("b", {}, n.toLocaleString()), h("span", {}, `places in ${scopeName(cfg.scope, cat)}`)),
      h("div", { class: "scoring-note" }, h("b", { style: "color:var(--text)" }, "Scoring fits the map. "),
        `Full 1,000 points within ${fmtDist(sc.perfectKm)}. Half points at ${fmtDist(rings[2].km)}. Bigger maps get bigger rings, so a whole-planet game stays fair.`),
      h("div", { class: "setup-go" },
        h("button", { class: "btn lime big", onclick: go, disabled: n === 0 || undefined }, n === 0 ? "No places match" : startLabel, n ? ic("arrow") : null),
        opts.mode === "solo" ? null : h("button", { class: "btn big", onclick: back }, "Cancel")));
    panel.append(h("div", { class: "setup-mobile-go", style: innerWidth > 820 ? "display:none" : "" },
      h("span", { class: "muted", style: "font-size:13px;line-height:1.2" }, `${n.toLocaleString()} places`),
      h("button", { class: "btn lime big", onclick: go, disabled: n === 0 || undefined }, startLabel)));

    const desktop = innerWidth > 820;
    getMap().fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
      padding: desktop ? { left: Math.min(660, innerWidth * 0.5) + 30, right: 80, top: 110, bottom: 190 } : 30,
      duration: 900, maxZoom: 8,
    });
  }
  render();
}

function seg<T extends string | number>(label: string, options: ([T, string] | [T, string, string])[], value: T, onPick: (v: T) => void, cls = ""): HTMLElement {
  return h("div", { class: `seg ${cls}`, role: "group", "aria-label": label },
    options.map(([v, text, sub]) => h("button", { "aria-pressed": String(v === value), onclick: () => onPick(v) }, text, sub ? h("small", {}, sub) : null)));
}

function countryPicker(cat: Catalog, code: string, onPick: (code: string) => void): HTMLElement {
  const current = cat.byCountry.get(code);
  const input = h("input", { value: current?.name ?? "", "aria-label": "Search a country", placeholder: "Search a country", autocomplete: "off" });
  const results = h("div", { class: "search-results", hidden: true });
  const wrap = h("div", { class: "search-wrap" },
    h("div", { class: "search-box" }, ic("search"), input, h("span", { style: "font-size:13px" }, current?.continent ?? "")), results);
  const update = () => {
    const q = input.value.trim().toLowerCase();
    if (!q || q === current?.name.toLowerCase()) { results.hidden = true; return; }
    const list = cat.index.countries
      .filter(c => c.name.toLowerCase().includes(q))
      .sort((a, b) => Number(!a.name.toLowerCase().startsWith(q)) - Number(!b.name.toLowerCase().startsWith(q)) || a.name.localeCompare(b.name))
      .slice(0, 8);
    results.replaceChildren(...(list.length ? list.map(c => h("button", { onclick: () => onPick(c.code) },
      h("span", {}, c.name), h("small", {}, `${Object.values(c.counts).reduce((s, x) => s + (x ?? 0), 0)} places`)))
      : [h("div", { class: "muted", style: "padding:10px" }, "No country by that name")]));
    results.hidden = false;
  };
  input.addEventListener("input", update);
  input.addEventListener("focus", () => input.select());
  input.addEventListener("keydown", e => {
    if (e.key === "Enter") (results.querySelector("button") as HTMLButtonElement | null)?.click();
    if (e.key === "Escape") { results.hidden = true; input.value = current?.name ?? ""; }
  });
  return wrap;
}

